import { computed, Signal } from '@angular/core';

import { AppError } from '@hglobal/common';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';

type BaseRequestStatusState = { requestStatus: RequestStatus };
type RequestStatus = 'idle' | 'pending' | 'success' | { error: AppError };

type StatusKey<Name extends string> = `${Name}Status`;
type PendingKey<Name extends string> = `is${Capitalize<Name>}Pending`;
type SuccessKey<Name extends string> = `is${Capitalize<Name>}Success`;
type ErrorKey<Name extends string> = `${Name}Error`;

type RequestStatusState<Name extends string> = {
  [Key in StatusKey<Name>]: Signal<RequestStatus>;
};

type RequestStatusSignals<Name extends string> = {
  [Key in PendingKey<Name>]: Signal<boolean>;
} & { [Key in SuccessKey<Name>]: Signal<boolean> } & {
  [Key in ErrorKey<Name>]: Signal<AppError | null>;
};

export function withRequestStatus<Name extends string>(requestNames: Name[] = [], initialState: Partial<RequestStatusState<Name>> = {}) {
  return signalStoreFeature(
    withState(createInitialState(requestNames, initialState)),
    withComputed((store: any) => createComputedSignals(store, requestNames)),
    withMethods(store => ({
      resetRequestStatus(request: Name) {
        patchState(store, { [`${request}Status`]: 'idle' } as RequestStatusState<Name> & BaseRequestStatusState);
      },
    })),
    withState<BaseRequestStatusState>({ requestStatus: 'idle' }),
    withComputed((store: any) => ({
      isLoading: computed(() => store.requestStatus() === 'pending'),
      isSuccess: computed(() => store.requestStatus() === 'success'),
      error: computed(() => {
        const status = store.requestStatus();
        return typeof status === 'object' ? status.error : null;
      }),
    })),
  );
}

function createInitialState<Name extends string>(
  requestNames: Name[],
  initialState: Partial<RequestStatusState<Name>>,
): RequestStatusState<Name> {
  return requestNames.reduce(
    (acc, cur) => ({ ...acc, [`${cur}Status`]: initialState[`${cur}Status`] || 'idle' }),
    {} as RequestStatusState<Name>,
  );
}

function createComputedSignals<Name extends string>(store: RequestStatusState<Name>, requestNames: Name[]): RequestStatusSignals<Name> {
  return requestNames.reduce((acc, cur) => {
    const statusKey: StatusKey<Name> = `${cur}Status`;
    const pendingKey: PendingKey<Name> = `is${capitalize(cur)}Pending`;
    const successKey: SuccessKey<Name> = `is${capitalize(cur)}Success`;
    const errorKey: ErrorKey<Name> = `${cur}Error`;

    const statusSignal = store[statusKey];

    return {
      ...acc,
      [pendingKey]: computed(() => statusSignal() === 'pending'),
      [successKey]: computed(() => statusSignal() === 'success'),
      [errorKey]: computed(() => (typeof statusSignal() === 'object' ? (statusSignal() as { error: AppError }).error : null)),
    };
  }, {} as RequestStatusSignals<Name>);
}

function capitalize<T extends string>(text: T): Capitalize<T> {
  return (text.charAt(0).toUpperCase() + text.substring(1)) as Capitalize<T>;
}

export function setPendingStatusFor<Name extends string>(
  name: Name,
  global: boolean = true,
): RequestStatusState<Name> & BaseRequestStatusState {
  const state = { [`${name}Status`]: 'pending' } as unknown as RequestStatusState<Name>;
  if (global) {
    return { ...state, requestStatus: 'pending' };
  }
  return { ...state, requestStatus: 'idle' };
}

export function setSuccessStatusFor<Name extends string>(
  name: Name,
  global: boolean = true,
): RequestStatusState<Name> & BaseRequestStatusState {
  const state = { [`${name}Status`]: 'success' } as unknown as RequestStatusState<Name>;
  if (global) {
    return { ...state, requestStatus: 'success' };
  }
  return { ...state, requestStatus: 'idle' };
}

export function setErrorStatusFor<Name extends string>(
  name: Name,
  error: AppError,
  global: boolean = true,
): RequestStatusState<Name> & BaseRequestStatusState {
  const state = { [`${name}Status`]: { error } } as unknown as RequestStatusState<Name>;
  if (global) {
    return { ...state, requestStatus: { error } };
  }
  return { ...state, requestStatus: 'idle' };
}
